// The on-screen KJP chart (INFS-1454). Pure SVG-string builder, no Vue, no DOM.
//
// WHY THIS EXISTS SEPARATELY FROM chartSvg.js
//
// chartSvg.js renders the chart that gets rasterised into the Excel export,
// and its design is deliberately the source workbook's: the same combo chart,
// the same Excel palette, the same white canvas, so the downloaded file looks
// like the workbook JKR already works from. That fidelity is confirmed intent
// (INFS-1454, 18 Sep 2026), pinned by a hash in scripts/verify-kjp.mjs, and
// must not move.
//
// This file renders the chart on screen, where none of that applies: it sits
// in a dark dashboard and answers to readability instead of to the workbook.
// Two renderers, one data layer — both read the same chartSeries.js defs (this
// one adds the screen-only monthly defs for R02/R06, see SCREEN_ONLY_SERIES)
// and the same chartGeometry.js maths, so the two pictures can differ in paint
// but never in figures. Nothing in this file may be imported by chartSvg.js, and
// nothing here may mutate the shared defs it borrows (see screenSeriesDefs).
//
// THE DUAL AXIS IS GONE (criterion 4)
//
// The workbook's R01 chart plots bars against a left axis and lines against a
// right one, because the annual ceiling runs ten-to-twelve times higher than
// the monthly bar. Two y-scales on one plot is the most misread construct in
// charting — the crossing point of a bar and a line becomes meaningless, since
// it is an artefact of two independently chosen scales. The export keeps it
// (fidelity); the screen splits the series across panels that each carry a
// single scale. Same four series, same numbers, no scale to misread.
//
// ONE SVG PER PANEL
//
// Each panel is emitted as its own self-contained <svg> rather than as bands
// of one tall drawing. That is what lets the panels sit side by side on a wide
// screen and fall back to stacked on a narrow one, using nothing but flex-wrap
// in KjpChart.vue — no breakpoints, no resize listener, no JS measuring the
// container. A single tall SVG could not do that: scaling it to the container
// width scales its height with it, which is exactly how the cumulative panel
// ended up below the fold before.

import { round2 } from '@/kjp'
import { KJP_CHART_TITLES, KJP_CHART_SERIES } from '@/components/kjp/chartSeries'
import { buildChartGeometry, axisTicks, absentSeriesKeys } from '@/components/kjp/chartGeometry'

// Dark-surface palette, validated as a categorical set against the #1b1b1b
// dashboard surface (lightness band, chroma floor, CVD separation ΔE 26.8,
// normal-vision ΔE 31.8, contrast ≥3:1 — all pass).
//
// Two hues only, and they are assigned by MEANING, not by series order:
// everything the contractor actually did is blue, everything the contract
// allows is orange. That mapping holds across both panels, so a reader learns
// it once. Adding a third series means taking the next slot from the
// documented categorical order, never inventing a hue.
export const SCREEN_COLORS = {
  actual: '#3987e5',
  allowance: '#d95926',
  surface: '#1b1b1b',
  textPrimary: '#ffffff',
  textSecondary: '#c3c2b7',
  textMuted: '#9a9993', // lifted from #8b8a84 so tick labels keep 4.5:1 on the scope-tinted frames
  grid: '#333333',
  axis: '#4a4a4a',
}

// Which hue each series carries, keyed by the series key in chartSeries.js.
const SERIES_ROLE = {
  bars: 'actual',
  cumulativeUsage: 'actual',
  monthlyAllocation: 'allowance',
  annualCeiling: 'allowance',
}

// One panel's own fixed-pixel box. Every panel is the same size, so a row of
// them lines up without the caller measuring anything.
const VB_W = 500
const PLOT_LEFT = 58
const PLOT_W = 424
const PLOT_TOP = 26
const PLOT_H = 132
const CAPTION_Y = 14
const MONTH_LABEL_Y = PLOT_TOP + PLOT_H + 14
const LEGEND_TOP = MONTH_LABEL_Y + 18
const LEGEND_ROW_H = 16
const VB_H = LEGEND_TOP + LEGEND_ROW_H + 10

// Blank band reserved at the top of the plot, above the tallest value.
//
// Without it the largest series lands exactly on the top gridline: in the
// monthly panel the allocation line IS the maximum, so it drew flush with the
// edge and read as a chart border rather than as the limit the bars are
// measured against. Implemented by shrinking the plot box and pushing it down,
// never by inflating the axis maximum — the tick labels still read the real
// figures, and chartGeometry.js (shared with the export) stays untouched.
const HEADROOM = 16

// Monthly series for the scopes whose workbook chart has none.
//
// R01's workbook chart plots monthly bars, so chartSeries.js carries them. R02
// and R06's workbook charts are cumulative lines only, and the export follows
// the workbook, so chartSeries.js must stay without them (INFS-1454 criterion
// 5: the exported image does not move). On screen the three scopes should read
// alike (Hanif feedback, 16 Oct), so the monthly pair is declared here instead,
// screen-only, in the same shape as a chartSeries.js def. The `source` and
// `unmeasuredSource` arrays already exist on every scope's model (src/kjp.js
// builds bars, barsUnmeasured and monthlyAllocation for all of them), so this
// adds no figures, only a second picture of numbers the model already holds;
// unmeasured months drop out of the bars exactly as R01's do, via the same
// unmeasuredSource handling in chartGeometry.js. Labels carry each scope's own
// unit (R02 km, R06 m²), the way the cumulative defs do.
//
// Never merged into KJP_CHART_SERIES, and chartSvg.js never reads this map.
const SCREEN_ONLY_SERIES = {
  R02: [
    { key: 'bars', label: 'Jumlah Road Shoulder (km)', type: 'bar',
      color: '#ED7D31', source: 'bars', unmeasuredSource: 'barsUnmeasured' },
    { key: 'monthlyAllocation', label: 'Peruntukan Syiling Bulanan (km)', type: 'bar',
      color: '#4472C4', source: 'monthlyAllocation' },
  ],
  R06: [
    { key: 'bars', label: 'Jumlah Garisan Jalan (m²)', type: 'bar',
      color: '#ED7D31', source: 'bars', unmeasuredSource: 'barsUnmeasured' },
    { key: 'monthlyAllocation', label: 'Peruntukan Siling Bulanan (m²)', type: 'bar',
      color: '#4472C4', source: 'monthlyAllocation' },
  ],
}

// Which series share a panel, per scope.
//
// Every scope now gets both panels on screen: R01 from its workbook defs, R02
// and R06 from SCREEN_ONLY_SERIES above. Still read off the defs rather than
// hardcoded per scope name: a panel is dropped when none of its keys exist for
// the scope, so a scope with no monthly defs would simply show the cumulative
// panel alone.
const PANEL_SPECS = [
  { key: 'monthly', title: 'Penggunaan Bulanan', keys: ['bars', 'monthlyAllocation'] },
  { key: 'cumulative', title: 'Unjuran Terkumpul', keys: ['cumulativeUsage', 'annualCeiling'] },
]

// The series defs for one panel, recoloured for the dark surface and forced
// onto a single axis.
//
// Three deliberate overrides of the shared def, all presentation-only and all
// applied to a COPY — chartSeries.js is shared with the export, and mutating
// it here would change the exported workbook image:
//   - `color`  the workbook's Excel palette gives way to the validated one
//   - `axis`   dropped, so buildChartGeometry collapses the panel to one scale
//   - `type`   monthlyAllocation is a bar in the workbook; on screen it is the
//              limit the bars are read against, and a limit is a reference
//              line, not a second bar competing for the same visual weight
function screenSeriesDefs(scopeKey, keys) {
  // Concatenated into a new array: neither source is mutated, and R01 (all in
  // the shared defs) gets an empty screen-only list.
  return (KJP_CHART_SERIES[scopeKey] || [])
    .concat(SCREEN_ONLY_SERIES[scopeKey] || [])
    .filter((def) => keys.includes(def.key))
    .map((def) => ({
      ...def,
      axis: undefined,
      type: def.key === 'monthlyAllocation' ? 'line' : def.type,
      color: SCREEN_COLORS[SERIES_ROLE[def.key] || 'actual'],
    }))
}

export function screenPanels(scopeKey) {
  return PANEL_SPECS
    .map((spec) => ({ ...spec, seriesDefs: screenSeriesDefs(scopeKey, spec.keys) }))
    .filter((panel) => panel.seriesDefs.length > 0)
}

export function screenChartTitle(scopeKey) {
  return KJP_CHART_TITLES[scopeKey] || ''
}

function esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// round2 before formatting, the same rule as KjpTable.vue's fmt and
// chartSvg.js's — a raw float would show a binary-rounding artefact the
// workbook does not have.
function fmt(v) {
  return round2(v).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

// Compact axis ticks: 60410.7 becomes "60.4k" so the axis gutter stays narrow
// and the labels never collide. The hover tooltip still carries the full
// figure, so no precision is lost to a reader who wants it.
function fmtAxis(v) {
  const n = round2(v)

  if (Math.abs(n) >= 1000) return `${round2(n / 1000)}k`

  return fmt(n)
}

// A bar with its top two corners rounded (4px, or less on a short/narrow bar),
// anchored square to the baseline. Rounding only the data end is the mark
// spec: a fully rounded rect would detach the bar from its axis.
function barPath({ x, y, width, height }) {
  const r = Math.min(4, width / 2, height)

  if (height <= 0) return ''
  if (r <= 0) return `M${x},${y + height} L${x},${y} L${x + width},${y} L${x + width},${y + height} Z`

  return (
    `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} ` +
    `L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} ` +
    `L${x + width},${y + height} Z`
  )
}

function panelGeometry(panel, model, monthCount) {
  return buildChartGeometry({
    seriesDefs: panel.seriesDefs,
    projections: (model && model.projections) || {},
    monthCount,
    // Top pushed down and height reduced by the same amount, so the baseline
    // still lands on the plot's bottom edge and the headroom appears above the
    // tallest value rather than below the shortest.
    plot: { left: PLOT_LEFT, top: PLOT_TOP + HEADROOM, width: PLOT_W, height: PLOT_H - HEADROOM },
  })
}

// Series a panel could not draw at all — every month unmeasured. Surfaced by
// KjpChart.vue's banner in words, never left as a silent empty area.
export function absentScreenSeriesNotes(model, scope) {
  const scopeKey = scope || (model && model.scope)
  const months = (model && model.months) || []
  if (!months.length) return []

  const notes = []

  screenPanels(scopeKey).forEach((panel) => {
    const geometry = panelGeometry(panel, model, months.length)
    const absent = absentSeriesKeys(geometry.series)

    geometry.series
      .filter((s) => absent.has(s.key))
      .forEach((s) => notes.push({ label: s.label, type: s.type }))
  })

  return notes
}

function buildPanelSvg(panel, model, months) {
  const geometry = panelGeometry(panel, model, months.length)
  const absent = absentSeriesKeys(geometry.series)
  const baselineY = PLOT_TOP + PLOT_H
  const parts = []

  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB_W} ${VB_H}" ` +
      `preserveAspectRatio="xMidYMid meet" font-family="sans-serif">`
  )
  // No background rect: the panel's own box (.kjp-chart-panel in KjpChart.vue)
  // paints the scope-tinted frame fill, and an opaque canvas here would cover
  // it. Text and marks were validated against the surface, and the tint is a
  // low-alpha wash of a scope colour over that same surface.

  // Each panel carries its own caption: side by side they are two separate
  // charts on two separate scales, and an unlabelled one would read as a
  // continuation of its neighbour.
  parts.push(
    `<text x="${PLOT_LEFT}" y="${CAPTION_Y}" font-size="11" font-weight="600" ` +
      `fill="${SCREEN_COLORS.textSecondary}">${esc(panel.title)}</text>`
  )

  // Recessive gridlines, solid (never dashed — dashing reads as noise and
  // competes with the data), tick labels in muted ink rather than any series
  // colour.
  axisTicks(geometry.axes.left.max, 4).forEach((value) => {
    const y = geometry.axes.left.yScale(value)

    parts.push(
      `<line x1="${PLOT_LEFT}" x2="${PLOT_LEFT + PLOT_W}" y1="${y}" y2="${y}" ` +
        `stroke="${SCREEN_COLORS.grid}" stroke-width="1" />` +
        `<text x="${PLOT_LEFT - 7}" y="${y + 3}" text-anchor="end" font-size="9" ` +
        `fill="${SCREEN_COLORS.textMuted}">${esc(fmtAxis(value))}</text>`
    )
  })

  parts.push(
    `<line x1="${PLOT_LEFT}" x2="${PLOT_LEFT + PLOT_W}" y1="${baselineY}" y2="${baselineY}" ` +
      `stroke="${SCREEN_COLORS.axis}" stroke-width="1" />`
  )

  geometry.series
    .filter((s) => s.type === 'bar')
    .forEach((series) => {
      series.bars.filter(Boolean).forEach((r) => {
        parts.push(`<path d="${barPath(r)}" fill="${series.color}" />`)
      })
    })

  geometry.series
    .filter((s) => s.type === 'line')
    .forEach((series) => {
      series.runs.forEach((d) => {
        parts.push(
          `<path d="${d}" fill="none" stroke="${series.color}" stroke-width="2" ` +
            `stroke-linecap="round" stroke-linejoin="round" />`
        )
      })
    })

  // Per-month hover band, spanning the plot. `<title>` gives a native tooltip
  // with every series' value for that month, which is the one hover mechanism
  // that survives being injected with v-html (there is no JS to bind). Wider
  // than any mark, so the hit target is never the thin line itself.
  const bandWidth = PLOT_W / months.length

  months.forEach((m, i) => {
    const lines = geometry.series.map((s) => {
      const values = (model.projections || {})[s.source] || []
      const unmeasured = s.unmeasuredSource ? (model.projections || {})[s.unmeasuredSource] : null
      const shown = unmeasured && unmeasured[i] ? '—' : fmt(values[i] || 0)

      return `${s.label}: ${shown}`
    })

    parts.push(
      `<rect class="kjp-hit" x="${PLOT_LEFT + bandWidth * i}" y="${PLOT_TOP}" width="${bandWidth}" ` +
        `height="${PLOT_H}" fill="transparent">` +
        `<title>${esc([m.label].concat(lines).join('\n'))}</title></rect>`
    )
  })

  // Month labels on every panel now, not just the bottom one: side by side the
  // panels no longer share a vertical axis line, so each needs its own ruler.
  months.forEach((m, i) => {
    parts.push(
      `<text x="${geometry.centers[i]}" y="${MONTH_LABEL_Y}" text-anchor="middle" font-size="8.5" ` +
        `fill="${SCREEN_COLORS.textMuted}">${esc(m.label)}</text>`
    )
  })

  // Legend, one entry per row, always present — each panel carries two series
  // and identity must never rest on colour alone. A series absent from the
  // drawing still gets a row, greyed and annotated, so the legend cannot imply
  // something is plotted when it is not.
  geometry.series.forEach((s, i) => {
    const y = LEGEND_TOP + i * LEGEND_ROW_H
    const isAbsent = absent.has(s.key)
    const color = isAbsent ? SCREEN_COLORS.axis : s.color

    if (s.type === 'bar') {
      parts.push(`<rect x="${PLOT_LEFT}" y="${y - 8}" width="10" height="10" rx="2" fill="${color}" />`)
    } else {
      parts.push(
        `<line x1="${PLOT_LEFT}" x2="${PLOT_LEFT + 11}" y1="${y - 3}" y2="${y - 3}" ` +
          `stroke="${color}" stroke-width="3" stroke-linecap="round" />`
      )
    }

    parts.push(
      `<text x="${PLOT_LEFT + 16}" y="${y}" font-size="10" ` +
        `fill="${isAbsent ? SCREEN_COLORS.textMuted : SCREEN_COLORS.textSecondary}">` +
        `${esc(s.label)}${isAbsent ? ' (tiada data)' : ''}</text>`
    )
  })

  parts.push('</svg>')

  return parts.join('')
}

// One self-contained <svg> per panel, in workbook order.
//
// Returns [] when there is nothing to draw, the same contract buildChartSvg
// honours — a caller doing `const panels = build(...); if (!panels.length)
// return` must not get an empty framed chart instead of nothing.
export function buildScreenPanels(model, scope) {
  const scopeKey = scope || (model && model.scope)
  const months = (model && model.months) || []

  if (!months.length) return []

  return screenPanels(scopeKey).map((panel) => ({
    key: panel.key,
    title: panel.title,
    svg: buildPanelSvg(panel, model, months),
  }))
}
